'use client'
import React, { useState, useMemo } from 'react'
import LinkComponent  from '@components/LinkComponent'
import Image          from 'next/image'
import CurrencySymbol from '@components/CurrencySymbol'
import { BiArea, BiSolidArea } from 'react-icons/bi'
import { FaBed, FaBath } from 'react-icons/fa'
import { IoMdVideocam } from 'react-icons/io'

// ─── Built-in label translations (no dictionary needed) ───────────────────────
const LABELS = {
  availableUnitsAt: { en: 'Available Units at ', th: 'ยูนิตที่มีอยู่ใน ', cn: '可用单元 ', ru: 'Доступные юниты в ', de: 'Verfügbare Einheiten bei ', it: 'Unità disponibili a ', fr: 'Unités disponibles à ', ja: '利用可能なユニット ', ko: '사용 가능한 유닛 ', es: 'Unidades disponibles en ', tr: 'Mevcut Birimler ', he: 'יחידות זמינות ב-', ar: 'الوحدات المتاحة في ' },
  forSale:          { en: 'For Sale',           th: 'ขาย',             cn: '出售',   ru: 'Продажа',         de: 'Zum Verkauf',        it: 'In vendita',    fr: 'À vendre',    ja: '売り物件',      ko: '매매',         es: 'En venta',      tr: 'Satılık',     he: 'למכירה',       ar: 'للبيع'          },
  forRent:          { en: 'For Rent',           th: 'เช่า',            cn: '出租',   ru: 'Аренда',          de: 'Zu vermieten',       it: 'In affitto',    fr: 'À louer',     ja: '賃貸物件',      ko: '임대',         es: 'En alquiler',   tr: 'Kiralık',     he: 'להשכרה',       ar: 'للإיجار'        },
  beds:             { en: 'beds',               th: 'ห้องนอน',         cn: '卧室',   ru: 'спален',          de: 'Schlafzimmer',       it: 'letti',         fr: 'lits',        ja: 'ベッド',        ko: '침실',         es: 'camas',         tr: 'yatak',       he: 'חדרי שינה',    ar: 'غرف نوم'        },
  baths:            { en: 'baths',              th: 'ห้องน้ำ',         cn: '浴室',   ru: 'ванных',          de: 'Bäder',              it: 'bagni',         fr: 'bains',       ja: 'バス',          ko: '욕실',         es: 'baños',         tr: 'banyo',       he: 'חדרי אמבטיה',  ar: 'حمامات'         },
  sqm:              { en: 'm²',                 th: 'ตร.ม.',           cn: '平方米', ru: 'м²',              de: 'm²',                 it: 'm²',            fr: 'm²',          ja: '㎡',            ko: '㎡',           es: 'm²',            tr: 'm²',          he: 'מ"ר',          ar: 'م²'             },
  studio:           { en: 'Studio',             th: 'สตูดิโอ',         cn: '开间',   ru: 'Студия',          de: 'Studio',             it: 'Monolocale',    fr: 'Studio',      ja: 'スタジオ',      ko: '스튜디오',      es: 'Estudio',       tr: 'Stüdyo',      he: 'סטודיו',       ar: 'استوديو'        },
  video:            { en: 'Video',              th: 'วิดีโอ',          cn: '视频',   ru: 'Видео',           de: 'Video',              it: 'Video',         fr: 'Vidéo',       ja: 'ビデオ',        ko: '비디오',       es: 'Vídeo',         tr: 'Video',       he: 'וידאו',        ar: 'فيديو'          },
  view:             { en: 'View',               th: 'ดู',              cn: '查看',   ru: 'Просмотр',        de: 'Ansehen',            it: 'Vedi',          fr: 'Voir',        ja: '見る',          ko: '보기',         es: 'Ver',           tr: 'Görüntüle',   he: 'צפה',          ar: 'عرض'            },
}

const t = (key, lang) => LABELS[key]?.[lang] ?? LABELS[key]?.en ?? key

// ─── CSS variable resolver ─────────────────────────────────────────────────────
const asColor = (val, fallback = 'inherit') => val ?? fallback

// ─── Aspect ratio padding map ─────────────────────────────────────────────────
const RATIO_PAD = { '4/3': '75%', '3/2': '66.6%', '16/9': '56.25%', '1/1': '100%' }

// ─── GA4 ──────────────────────────────────────────────────────────────────────
async function fireSelectProperty(property, position) {
  try {
    const { sendGAEvent } = await import('@next/third-parties/google')
    sendGAEvent('event', 'select_property', {
      property_id:       property.ref || property.id,
      property_name:     property.name,
      property_type:     property.metaCategory,
      property_location: property.districts?.[0]?.subdistrictName?.['en'],
      property_saletype: property.saleType,
      property_bedrooms: property.subcategory?.toLowerCase() === 'studio' ? 'studio' : property.bedrooms || undefined,
      property_livingarea: property.livingArea || undefined,
      property_landarea:   property.landArea   || undefined,
      property_saleprice:  property.salePrice  || undefined,
      property_rentprice:  property.rentPrice  || undefined,
      property_position:   position,
      property_source:     'project_units',
    })
  } catch (e) {
    console.error('GA event error:', e)
  }
}

// ─── Price blocks (shared across all card layouts) ────────────────────────────
function PriceBlocks({ property, so, tab, fontSize }) {
  const isRent      = tab === 'rent'
  const oldPrice    = isRent ? property.oldRentPrice  : property.oldSalePrice
  const currentPrice = isRent ? property.rentPrice    : property.salePrice
  const showOld     = isRent ? so.showOldRentPrice    : so.showOldPrice
  const showCurrent = isRent ? so.showRentPrice       : so.showPrice

  return (
    <>
      {showOld && oldPrice && (
        <span style={{ textDecoration: 'line-through', color: asColor(so.oldPriceColor), fontSize: fontSize ?? '0.85rem' }}>
          <CurrencySymbol />{oldPrice}
        </span>
      )}
      {showCurrent && currentPrice && (
        <span style={{ color: asColor(so.priceColor), fontWeight: so.priceFw, fontSize: so.priceFontSize }}>
          <CurrencySymbol />{currentPrice}
        </span>
      )}
    </>
  )
}

// ─── Card for grid layout ─────────────────────────────────────────────────────
function GridCard({ property, lang, so, tab, onCardClick }) {
  const shadowMap = { none: 'none', sm: '0 1px 3px rgba(0,0,0,0.1)', md: '0 4px 12px rgba(0,0,0,0.15)' }
  const pad       = RATIO_PAD[so.imageRatio] ?? '75%'

  return (
    <LinkComponent href={`/properties/${property.slug}`} onClick={onCardClick}>
      <div style={{
        borderRadius: so.cardRadius,
        border:       so.cardBorder ? '1px solid #e2e8f0' : 'none',
        boxShadow:    shadowMap[so.cardShadow] ?? shadowMap.sm,
        overflow:     'hidden',
        background:   '#fff',
      }}>
        {/* Image */}
        <div style={{ position: 'relative', paddingTop: pad, borderRadius: `${so.imageRadius} ${so.imageRadius} 0 0`, overflow: 'hidden' }}>
          {property.cover && (
            <Image className="object-cover" src={property.cover} fill alt={property.slug ?? ''} />
          )}
          {so.showVideoBadge && property.videoUrl && (
            <div style={{ position: 'absolute', bottom: 5, left: 5, background: '#991b1b', color: '#fff', fontSize: 11, padding: '2px 7px', borderRadius: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
              <IoMdVideocam /> {t('video', lang)}
            </div>
          )}
        </div>

        {/* Body */}
        <div style={{ padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: 3 }}>
          {so.showName && (
            <p style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', margin: 0 }}>
              {property.name}
            </p>
          )}
          <PriceBlocks property={property} so={so} tab={tab} />
          <div style={{ display: 'flex', gap: 10, fontSize: '0.8rem', color: '#64748b', flexWrap: 'wrap' }}>
            {so.showBeds  && (property.bedrooms  > 0) && <span><FaBed  style={{ display:'inline',marginRight:3 }}/>{property.isStudio ? t('studio',lang) : property.bedrooms} {t('beds',lang)}</span>}
            {so.showBaths && (property.bathrooms > 0) && <span><FaBath style={{ display:'inline',marginRight:3 }}/>{property.bathrooms} {t('baths',lang)}</span>}
            {so.showLivingArea && property.livingArea > 0 && <span><BiArea style={{ display:'inline',marginRight:3 }}/>{property.livingArea} {t('sqm',lang)}</span>}
            {so.showLandArea   && property.landArea   > 0 && <span><BiSolidArea style={{ display:'inline',marginRight:3 }}/>{property.landArea} {t('sqm',lang)}</span>}
          </div>
          {so.showViewButton && (
            <div style={{ marginTop: 6 }}>
              <ViewBtn so={so} lang={lang} />
            </div>
          )}
        </div>
      </div>
    </LinkComponent>
  )
}

// ─── Row card (desktop list row) ──────────────────────────────────────────────
function RowCard({ property, lang, so, tab, onCardClick }) {
  const pad = RATIO_PAD[so.imageRatio] ?? '75%'

  return (
    <LinkComponent href={`/properties/${property.slug}`} onClick={onCardClick}>
      <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: '1px solid #f1f5f9' }}>
        {/* Thumbnail */}
        <div style={{ width: 110, minWidth: 110, borderRadius: so.imageRadius, overflow: 'hidden', background: '#e2e8f0', position: 'relative' }}>
          <div style={{ paddingTop: pad }} />
          {property.cover && (
            <Image className="object-cover" src={property.cover} fill alt={property.slug ?? ''} />
          )}
          {so.showVideoBadge && property.videoUrl && (
            <div style={{ position: 'absolute', bottom: 3, left: 3, background: '#991b1b', color: '#fff', fontSize: 10, padding: '1px 5px', borderRadius: 3, display: 'flex', alignItems: 'center', gap: 3 }}>
              <IoMdVideocam /> {t('video', lang)}
            </div>
          )}
        </div>

        {/* Info */}
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {so.showName && (
            <p style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', margin: 0 }}>
              {property.name}
            </p>
          )}
          <PriceBlocks property={property} so={so} tab={tab} fontSize="0.82rem" />
          <div style={{ display: 'flex', gap: 12, fontSize: '0.8rem', color: '#64748b', flexWrap: 'wrap', marginTop: 2 }}>
            {so.showBeds  && (property.bedrooms  > 0) && <span><FaBed  style={{ display:'inline',marginRight:3 }}/>{property.isStudio ? t('studio',lang) : property.bedrooms} {t('beds',lang)}</span>}
            {so.showBaths && (property.bathrooms > 0) && <span><FaBath style={{ display:'inline',marginRight:3 }}/>{property.bathrooms} {t('baths',lang)}</span>}
            {so.showLivingArea && property.livingArea > 0 && <span><BiArea style={{ display:'inline',marginRight:3 }}/>{property.livingArea} {t('sqm',lang)}</span>}
            {so.showLandArea   && property.landArea   > 0 && <span><BiSolidArea style={{ display:'inline',marginRight:3 }}/>{property.landArea} {t('sqm',lang)}</span>}
          </div>
        </div>

        {/* Button */}
        {so.showViewButton && (
          <div style={{ flexShrink: 0 }}>
            <ViewBtn so={so} lang={lang} />
          </div>
        )}
      </div>
    </LinkComponent>
  )
}

// ─── Mobile card (full-width stacked) ─────────────────────────────────────────
function MobileCard({ property, lang, so, tab, onCardClick }) {
  const pad = RATIO_PAD[so.imageRatio] ?? '75%'

  return (
    <LinkComponent href={`/properties/${property.slug}`} onClick={onCardClick}>
      <div style={{ borderRadius: so.cardRadius, border: so.cardBorder ? '1px solid #e2e8f0' : 'none', overflow: 'hidden', marginTop: 8 }}>
        {/* Image */}
        <div style={{ position: 'relative', paddingTop: pad }}>
          {property.cover && (
            <Image className="object-cover" src={property.cover} fill alt={property.slug ?? ''} />
          )}
          {so.showVideoBadge && property.videoUrl && (
            <div style={{ position: 'absolute', bottom: 5, left: 5, background: '#991b1b', color: '#fff', fontSize: 11, padding: '2px 7px', borderRadius: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
              <IoMdVideocam /> {t('video', lang)}
            </div>
          )}
        </div>

        {/* Body */}
        <div style={{ padding: 8, display: 'flex', flexDirection: 'column', gap: 3 }}>
          {so.showName && (
            <p style={{ fontWeight: 600, margin: 0, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
              {property.name}
            </p>
          )}
          <PriceBlocks property={property} so={so} tab={tab} fontSize="0.82rem" />
          <div style={{ display: 'flex', gap: 10, fontSize: '0.8rem', color: '#64748b', flexWrap: 'wrap' }}>
            {so.showBeds  && (property.bedrooms  > 0) && <span><FaBed  style={{ display:'inline',marginRight:3 }}/>{property.isStudio ? t('studio',lang) : property.bedrooms} {t('beds',lang)}</span>}
            {so.showBaths && (property.bathrooms > 0) && <span><FaBath style={{ display:'inline',marginRight:3 }}/>{property.bathrooms} {t('baths',lang)}</span>}
            {so.showLivingArea && property.livingArea > 0 && <span><BiArea style={{ display:'inline',marginRight:3 }}/>{property.livingArea} {t('sqm',lang)}</span>}
            {so.showLandArea   && property.landArea   > 0 && <span><BiSolidArea style={{ display:'inline',marginRight:3 }}/>{property.landArea} {t('sqm',lang)}</span>}
          </div>
          {so.showViewButton && (
            <div style={{ marginTop: 4 }}>
              <ViewBtn so={so} lang={lang} fullWidth />
            </div>
          )}
        </div>
      </div>
    </LinkComponent>
  )
}

// ─── View button ──────────────────────────────────────────────────────────────
function ViewBtn({ so, lang, fullWidth }) {
  const label  = so.btnLabel?.trim() || t('view', lang)
  const bg     = asColor(so.btnColor, 'var(--color-main)')
  const color  = asColor(so.btnTextColor, '#ffffff')
  const base   = { display: 'inline-block', cursor: 'pointer', border: 'none', padding: '6px 16px', fontSize: '0.875rem', fontWeight: 600, width: fullWidth ? '100%' : undefined, textAlign: 'center', transition: 'opacity 0.15s' }

  if (so.btnStyle === 'outlined') return <span style={{ ...base, background: 'transparent', color: bg, border: `1.5px solid ${bg}`, borderRadius: 6 }}>{label}</span>
  if (so.btnStyle === 'pill')     return <span style={{ ...base, background: bg, color, borderRadius: 999 }}>{label}</span>
  return <span style={{ ...base, background: bg, color, borderRadius: 6 }}>{label}</span>
}

// ─── Tab button ───────────────────────────────────────────────────────────────
function TabBtn({ label, count, isActive, onClick, so }) {
  const active   = asColor(so.tabActiveColor, 'var(--color-main)')
  const inactive = asColor(so.tabInactiveColor, 'var(--color-secondary)')
  const display  = so.showCount !== false ? `${label} (${count})` : label

  if (so.tabStyle === 'filled') return (
    <button onClick={onClick} style={{ padding: '7px 18px', cursor: 'pointer', border: 'none', borderRadius: 6, background: isActive ? active : '#f1f5f9', color: isActive ? '#fff' : inactive, fontWeight: isActive ? 700 : 400, fontSize: '0.875rem', transition: 'all 0.15s' }}>
      {display}
    </button>
  )
  if (so.tabStyle === 'underline') return (
    <button onClick={onClick} style={{ padding: '7px 18px', cursor: 'pointer', border: 'none', borderBottom: `2px solid ${isActive ? active : 'transparent'}`, background: 'transparent', color: isActive ? active : inactive, fontWeight: isActive ? 700 : 400, fontSize: '0.875rem', borderRadius: 0, transition: 'all 0.15s' }}>
      {display}
    </button>
  )
  // pill (default)
  return (
    <button onClick={onClick} style={{ padding: '7px 18px', cursor: 'pointer', border: `1.5px solid ${active}`, borderRadius: 999, background: isActive ? active : 'transparent', color: isActive ? '#fff' : inactive, fontWeight: isActive ? 700 : 400, fontSize: '0.875rem', transition: 'all 0.15s' }}>
      {display}
    </button>
  )
}

// ─── Pagination ───────────────────────────────────────────────────────────────
function Pagination({ page, total, perPage, onChange, so }) {
  const pageCount = Math.ceil(total / perPage)
  if (pageCount <= 1) return null
  const active = asColor(so.tabActiveColor, 'var(--color-main)')
  return (
    <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginTop: 14, flexWrap: 'wrap' }}>
      {Array.from({ length: pageCount }, (_, i) => (
        <button key={i} onClick={() => onChange(i)} style={{ width: 32, height: 32, borderRadius: 6, border: `1.5px solid ${active}`, background: page === i ? active : 'transparent', color: page === i ? '#fff' : active, fontWeight: page === i ? 700 : 400, cursor: 'pointer', fontSize: '0.875rem' }}>
          {i + 1}
        </button>
      ))}
    </div>
  )
}

// ─── Main component ───────────────────────────────────────────────────────────
const ProjectProperties = ({
  name,
  properties      = [],
  saleProperties  = 0,
  rentProperties  = 0,
  lang            = 'en',
  titleOverride   = null,
  titleColor      = 'var(--color-main)',
  titleFw         = '600',
  titleSize       = '1.25rem',
  styleOverrides  = {},
}) => {
  const [activeTab,  setActiveTab]  = useState('sale')
  const [salePage,   setSalePage]   = useState(0)
  const [rentPage,   setRentPage]   = useState(0)

  const so = {
    tabStyle:          'pill',
    tabActiveColor:    'var(--color-main)',
    tabInactiveColor:  'var(--color-secondary)',
    showCount:         true,
    saleLabel:         t('forSale', lang),
    rentLabel:         t('forRent', lang),
    cardLayout:        'row',
    cardRadius:        '0.5rem',
    cardBorder:        true,
    cardShadow:        'sm',
    imageRatio:        '4/3',
    imageRadius:       '0.375rem',
    showPrice:         true,
    showOldPrice:      true,
    showRentPrice:     true,
    showOldRentPrice:  true,
    showName:          true,
    showBeds:          true,
    showBaths:         true,
    showLivingArea:    true,
    showLandArea:      true,
    showVideoBadge:    true,
    showViewButton:    true,
    priceColor:        'var(--thirdcolor)',
    priceFw:           '700',
    priceFontSize:     '1rem',
    oldPriceColor:     '#9ca3af',
    btnStyle:          'filled',
    btnColor:          'var(--color-main)',
    btnTextColor:      '#ffffff',
    btnLabel:          '',
    showPagination:    true,
    itemsPerPage:      10,
    ...styleOverrides,
  }

  const saleLabel = so.saleLabel || t('forSale', lang)
  const rentLabel = so.rentLabel || t('forRent', lang)

  const saleProps = useMemo(() => properties.filter(p => p.saleType === 'sale' || p.saleType === 'salerent'), [properties])
  const rentProps = useMemo(() => properties.filter(p => p.saleType === 'rent' || p.saleType === 'salerent'), [properties])

  const perPage   = Number(so.itemsPerPage) || 10
  const salePaged = so.showPagination !== false ? saleProps.slice(salePage * perPage, (salePage + 1) * perPage) : saleProps
  const rentPaged = so.showPagination !== false ? rentProps.slice(rentPage * perPage, (rentPage + 1) * perPage) : rentProps

  const shown   = activeTab === 'sale' ? salePaged : rentPaged
  const total   = activeTab === 'sale' ? saleProps.length : rentProps.length
  const page    = activeTab === 'sale' ? salePage  : rentPage
  const setPage = activeTab === 'sale' ? setSalePage : setRentPage

  const titleText = titleOverride || `${t('availableUnitsAt', lang)}${name}`

  const renderCard = (property, index) => {
    const position = page * perPage + index + 1
    const onCardClick = () => fireSelectProperty(property, position)
    const props = { key: property.id, property, lang, so, tab: activeTab, onCardClick }

    if (so.cardLayout === 'grid') return <GridCard {...props} />
    return (
      <>
        <div className="sm:hidden" key={`mob-${property.id}`}>
          <MobileCard {...props} />
        </div>
        <div className="hidden sm:block" key={`desk-${property.id}`}>
          <RowCard {...props} />
        </div>
      </>
    )
  }

  return (
    <div style={{ marginBottom: 8 }}>

      {/* Section title */}
      <h2 style={{ color: asColor(titleColor), fontWeight: titleFw, fontSize: titleSize, marginBottom: 8, marginTop: 0 }}>
        {titleText}
      </h2>

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        <TabBtn
          label={saleLabel}
          count={saleProperties || saleProps.length}
          isActive={activeTab === 'sale'}
          onClick={() => { setActiveTab('sale'); setSalePage(0) }}
          so={so}
        />
        <TabBtn
          label={rentLabel}
          count={rentProperties || rentProps.length}
          isActive={activeTab === 'rent'}
          onClick={() => { setActiveTab('rent'); setRentPage(0) }}
          so={so}
        />
      </div>

      {/* Property list */}
      {shown.length === 0 ? (
        <p style={{ color: '#94a3b8', fontSize: '0.875rem', fontStyle: 'italic' }}>—</p>
      ) : so.cardLayout === 'grid' ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 14 }}>
          {shown.map((p, i) => renderCard(p, i))}
        </div>
      ) : (
        <div>{shown.map((p, i) => renderCard(p, i))}</div>
      )}

      {/* Pagination */}
      {so.showPagination !== false && (
        <Pagination page={page} total={total} perPage={perPage} onChange={setPage} so={so} />
      )}

    </div>
  )
}

export default ProjectProperties